import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
} from '@bookaburra/ui';
import { ArrowRight, Bot, Send, Sparkles, User as UserIcon } from 'lucide-react';
import {
  postChatStep,
  postChatText,
  type ChatChoice,
} from '../booking-flow/chat-api';
import { fetchBookingTenant } from '../booking-flow/api';
import {
  cancelMyBooking,
  listMyBookings,
  type MyBooking,
} from '../booking-flow/me-bookings-api';
import { authClient } from '../auth/client';
import { AppHeader } from '../components/AppHeader';
import { LocaleSwitcher } from '../components/LocaleSwitcher';
import { useDocumentTitle } from '../lib/use-document-title';
import { getPhrases, formatWhenLocalised } from '../chat/translations';

interface ChatSearch {
  locale?: string;
}

export const Route = createFileRoute('/chat/$slug')({
  validateSearch: (search: Record<string, unknown>): ChatSearch => ({
    locale: typeof search.locale === 'string' ? search.locale : undefined,
  }),
  component: ChatPage,
});

/**
 * Client-only actions (never sent to the chat backend). Used to drive
 * the signed-in cancellation flow on top of the server state machine:
 *
 *   Modify/Cancel intent → look up the user's upcoming bookings at
 *   this tenant → tap one → "really cancel?" → call the cancel API.
 */
type ClientAction =
  | { type: 'show-bookings' }
  | { type: 'cancel-pick'; bookingId: string; serviceName: string; when: string }
  | { type: 'cancel-confirm'; bookingId: string; serviceName: string; when: string }
  | { type: 'restart-client' };

interface ClientChoice {
  label: string;
  spokenAs?: string;
  clientAction: ClientAction;
}

interface Turn {
  id: number;
  role: 'assistant' | 'user';
  content: string;
  choices?: ChatChoice[];
  clientChoices?: ClientChoice[];
  cta?: { label: string; href: string };
  consumed?: boolean;
}

function ChatPage() {
  const { slug } = Route.useParams();
  const { locale: searchLocale } = Route.useSearch();
  const locale = searchLocale ?? 'en-AU';
  const p = getPhrases(locale);

  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `${tenantQ.data.tenant.name} Booking Assistant` : null,
  );

  const { data: session } = authClient.useSession();
  const signedIn = Boolean(session?.user);

  const [turns, setTurns] = useState<Turn[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Boot the conversation with the initial step. Re-issued when the
  // locale changes so the assistant's reply switches language.
  const initQ = useQuery({
    queryKey: ['chat-init', slug, locale],
    queryFn: () => postChatStep(slug, { type: 'init' }, locale),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Reset the turn log on locale switch — the previous bubbles are
  // stale (wrong language). useEffect on init data below repopulates.
  useEffect(() => {
    setTurns([]);
  }, [locale]);

  useEffect(() => {
    if (!initQ.data || turns.length > 0) return;
    setTurns([
      {
        id: Date.now(),
        role: 'assistant',
        content: initQ.data.reply,
        choices: initQ.data.choices,
        clientChoices: signedIn
          ? [
              {
                label: p.showMyBookings,
                spokenAs: p.showMyBookings,
                clientAction: { type: 'show-bookings' },
              },
            ]
          : undefined,
        cta: initQ.data.cta,
      },
    ]);
  }, [initQ.data, signedIn, turns.length, p.showMyBookings]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [turns.length]);

  /** Mark the last unconsumed assistant turn as consumed and append an
   *  echo of the user's pick. */
  function appendUserEcho(label: string) {
    setTurns((prev) => {
      const updated = prev.map((t, i) =>
        i === prev.length - 1 && t.role === 'assistant'
          ? { ...t, consumed: true }
          : t,
      );
      return [
        ...updated,
        { id: Date.now(), role: 'user' as const, content: label },
      ];
    });
  }

  function appendAssistant(turn: Omit<Turn, 'id' | 'role'>) {
    setTurns((prev) => [
      ...prev,
      { id: Date.now() + 1, role: 'assistant', ...turn },
    ]);
  }

  function appendRestart() {
    // Re-issue the initial intent buttons as a fresh assistant turn so
    // the user can start a new flow without reloading the page. We use
    // the cached init result rather than calling the server again, and
    // re-append the signed-in "Show my bookings" shortcut.
    const init = initQ.data;
    if (!init) return;
    appendAssistant({
      content: init.reply,
      choices: init.choices,
      clientChoices: signedIn
        ? [
            {
              label: p.showMyBookings,
              spokenAs: p.showMyBookings,
              clientAction: { type: 'show-bookings' },
            },
          ]
        : undefined,
      cta: init.cta,
    });
  }

  const stepMut = useMutation({
    mutationFn: ({ choice }: { choice: ChatChoice }) =>
      postChatStep(slug, choice.action, locale),
    onMutate: ({ choice }) => {
      appendUserEcho(choice.spokenAs ?? choice.label);
    },
    onSuccess: (r) => {
      appendAssistant({ content: r.reply, choices: r.choices, cta: r.cta });
    },
  });

  // Free-typed text goes through the cheap LLM. If the model can map
  // the words to one of the visible buttons we treat it like a click
  // (state-machine advances). Otherwise the model composes a brief
  // reply and we keep the existing buttons visible.
  const textMut = useMutation({
    mutationFn: ({ text, choices }: { text: string; choices: ChatChoice[] }) =>
      postChatText(slug, text, choices, locale),
    onMutate: ({ text }) => appendUserEcho(text),
    onSuccess: (r) => {
      appendAssistant({
        content: r.matchedLabel
          ? r.reply
          : r.reply,
        choices: r.choices,
        cta: r.cta,
      });
    },
  });

  /** Open the cancel flow for the signed-in user: fetch their upcoming
   *  bookings at this tenant and render them as buttons. */
  const cancelListMut = useMutation({
    mutationFn: listMyBookings,
    onSuccess: (r) => {
      const tenant = r.tenants.find((t) => t.tenant.slug === slug);
      const upcoming: MyBooking[] = (tenant?.bookings ?? []).filter(
        (b) => b.canManage,
      );
      if (!tenant || upcoming.length === 0) {
        appendAssistant({
          content: p.noUpcomingBookingsHere,
          clientChoices: [
            { label: p.startOver, clientAction: { type: 'restart-client' } },
          ],
        });
        return;
      }
      const tz = tenant.tenant.timezone;
      appendAssistant({
        content: p.hereAreYourBookings,
        clientChoices: upcoming.map((b) => {
          const when = formatWhenLocalised(b.rangeStart, tz, locale);
          return {
            label: `${b.serviceName} · ${when}`,
            spokenAs: p.bookingOn(b.serviceName, when),
            clientAction: {
              type: 'cancel-pick' as const,
              bookingId: b.id,
              serviceName: b.serviceName,
              when,
            },
          };
        }),
      });
    },
    onError: () => {
      appendAssistant({
        content: p.couldntLoadBookings,
        clientChoices: [
          { label: p.startOver, clientAction: { type: 'restart-client' } },
        ],
      });
    },
  });

  const doCancelMut = useMutation({
    mutationFn: (bookingId: string) => cancelMyBooking(bookingId),
    onSuccess: () => {
      appendAssistant({
        content: p.cancelledConfirmation,
        clientChoices: [
          { label: p.startOver, clientAction: { type: 'restart-client' } },
        ],
      });
    },
    onError: () => {
      appendAssistant({
        content: p.somethingWentWrongCancelling,
        clientChoices: [
          { label: p.startOver, clientAction: { type: 'restart-client' } },
        ],
      });
    },
  });

  const anyPending =
    stepMut.isPending ||
    cancelListMut.isPending ||
    doCancelMut.isPending ||
    textMut.isPending;

  function onChoice(choice: ChatChoice) {
    if (anyPending) return;
    // Intercept the modify/cancel intents — both currently dead-end on
    // the server with "open the email link". If the user is signed in,
    // we can do better by showing their actual bookings.
    if (
      choice.action.type === 'intent' &&
      (choice.action.value === 'modify' || choice.action.value === 'cancel') &&
      signedIn
    ) {
      appendUserEcho(choice.spokenAs ?? choice.label);
      appendAssistant({
        content: p.lookingUpBookings,
      });
      cancelListMut.mutate();
      return;
    }
    // Same intents but signed out: fall back to the server reply
    // (which will say "use the email link") AND nudge a sign-in.
    if (
      choice.action.type === 'intent' &&
      (choice.action.value === 'modify' || choice.action.value === 'cancel') &&
      !signedIn
    ) {
      stepMut.mutate(
        { choice },
        {
          onSuccess: () => {
            appendAssistant({
              content: p.alreadyHaveAccount,
              cta: {
                label: p.signIn,
                href: `/signin?redirect=${encodeURIComponent(`/chat/${slug}`)}`,
              },
            });
          },
        },
      );
      return;
    }
    stepMut.mutate({ choice });
  }

  function onClientChoice(choice: ClientChoice) {
    if (anyPending) return;
    appendUserEcho(choice.spokenAs ?? choice.label);
    const action = choice.clientAction;
    switch (action.type) {
      case 'show-bookings': {
        appendAssistant({ content: p.lookingUpBookings });
        cancelListMut.mutate();
        return;
      }
      case 'cancel-pick': {
        appendAssistant({
          content: p.reallyCancel(action.serviceName, action.when),
          clientChoices: [
            {
              label: p.yesCancelIt,
              spokenAs: p.yesCancelIt,
              clientAction: {
                type: 'cancel-confirm',
                bookingId: action.bookingId,
                serviceName: action.serviceName,
                when: action.when,
              },
            },
            {
              label: p.noKeepIt,
              spokenAs: p.noKeepIt,
              clientAction: { type: 'restart-client' },
            },
          ],
        });
        return;
      }
      case 'cancel-confirm': {
        doCancelMut.mutate(action.bookingId);
        return;
      }
      case 'restart-client': {
        appendRestart();
        return;
      }
    }
  }

  const [input, setInput] = useState('');

  /**
   * Free-typed text. We send it to the server's `/text` endpoint
   * which uses the cheap intent-router LLM to either route to one of
   * the visible buttons (state machine advances) or compose a brief
   * conversational reply that still surfaces the buttons.
   */
  function onSubmitText(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || anyPending) return;
    setInput('');
    const lastAssistant = [...turns].reverse().find(
      (t) => t.role === 'assistant' && !t.consumed,
    );
    const choices = lastAssistant?.choices ?? [];
    textMut.mutate({ text, choices });
  }

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader
        tenant={
          tenantQ.data
            ? {
                slug: tenantQ.data.tenant.slug,
                name: tenantQ.data.tenant.name,
                timezone: tenantQ.data.tenant.timezone,
              }
            : undefined
        }
      />
      <main className="mx-auto flex max-w-2xl flex-col px-6 py-8 sm:px-12">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
              <Sparkles className="h-3.5 w-3.5" /> {p.bookingAssistantEyebrow}
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight">
              {tenantQ.data
                ? p.bookWithTenant(tenantQ.data.tenant.name)
                : p.bookingAssistantFallback}
            </h1>
            <p className="text-sm text-muted-foreground">
              {p.pickAnOption}
              {tenantQ.data?.tenant.hasVoiceAgent ? (
                <>
                  {' '}
                  <Link
                    to="/voice/$slug"
                    params={{ slug }}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {p.preferToTalk}
                  </Link>
                </>
              ) : null}
            </p>
          </div>
          <LocaleSwitcher activeLocale={locale} />
        </header>

      <div
        ref={scrollRef}
        className="flex-1 space-y-4 overflow-y-auto rounded-2xl border border-brand-sage/15 bg-card p-4 shadow-brand sm:max-h-[50vh] sm:p-6"
      >
        {initQ.isLoading ? (
          <div className="flex gap-3 text-sm text-muted-foreground">
            <Bot className="mt-0.5 h-4 w-4" />
            <span className="animate-pulse">{p.connecting}</span>
          </div>
        ) : null}
        {turns.map((t) => (
          <TurnRow
            key={t.id}
            turn={t}
            onChoice={onChoice}
            onClientChoice={onClientChoice}
            disabled={anyPending}
          />
        ))}
        {anyPending ? (
          <div className="flex gap-3 text-sm text-muted-foreground">
            <Bot className="mt-0.5 h-4 w-4" />
            <span className="animate-pulse">…</span>
          </div>
        ) : null}
        {initQ.error || stepMut.error ? (
          <Alert variant="destructive">
            <AlertTitle>{p.couldntReachAssistant}</AlertTitle>
            <AlertDescription>
              {(initQ.error || stepMut.error) instanceof Error
                ? ((initQ.error || stepMut.error) as Error).message
                : p.tryAgain}
            </AlertDescription>
          </Alert>
        ) : null}
      </div>

      <form
        onSubmit={onSubmitText}
        className="mt-3 flex items-center gap-2 rounded-xl border border-brand-sage/15 bg-card px-3 py-2 shadow-brand"
      >
        <Input
          aria-label={p.typeAMessage}
          placeholder={p.typeAMessage}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={anyPending}
          className="border-none bg-card shadow-none focus-visible:ring-0"
        />
        <Button
          type="submit"
          size="sm"
          disabled={!input.trim() || anyPending}
          aria-label={p.sendAria}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          {p.cancellationsAlsoWork}
        </p>
      </main>
    </div>
  );
}

function TurnRow({
  turn,
  onChoice,
  onClientChoice,
  disabled,
}: {
  turn: Turn;
  onChoice: (c: ChatChoice) => void;
  onClientChoice: (c: ClientChoice) => void;
  disabled: boolean;
}) {
  const isUser = turn.role === 'user';
  const showServer =
    !isUser && !turn.consumed && turn.choices && turn.choices.length > 0;
  const showClient =
    !isUser && !turn.consumed && turn.clientChoices && turn.clientChoices.length > 0;
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
          isUser ? 'bg-secondary text-foreground' : 'bg-primary text-primary-foreground'
        }`}
      >
        {isUser ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${
          isUser ? 'bg-secondary' : 'bg-primary/10'
        }`}
      >
        <p className="whitespace-pre-wrap">{turn.content}</p>
        {showServer ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {turn.choices!.map((c, i) => (
              <Button
                key={i}
                variant="outline"
                size="sm"
                onClick={() => onChoice(c)}
                disabled={disabled}
              >
                {c.label}
              </Button>
            ))}
          </div>
        ) : null}
        {showClient ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {turn.clientChoices!.map((c, i) => (
              <Button
                key={i}
                variant="outline"
                size="sm"
                onClick={() => onClientChoice(c)}
                disabled={disabled}
              >
                {c.label}
              </Button>
            ))}
          </div>
        ) : null}
        {!isUser && turn.cta ? (
          <div className="mt-3">
            <a
              href={turn.cta.href}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {turn.cta.label} <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
